import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const apiClient = axios.create({
  baseURL: API,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

// ============ Public ============
export const fetchCategories = async () => {
  const { data } = await apiClient.get('/categories');
  return data;
};

export const fetchProducts = async (category, options = {}) => {
  const params = {};
  if (category && category !== 'all') params.category = category;
  if (options.featured) params.featured = true;
  const { data } = await apiClient.get('/products', { params });
  return data;
};

export const submitInquiry = async (payload) => {
  const { data } = await apiClient.post('/inquiries', payload);
  return data;
};

// Quotes (public)
export const submitQuote = async (payload) => {
  const { data } = await apiClient.post('/quotes', payload);
  return data;
};

// Admin: Quotes
export const fetchAdminQuotes = async (status) => {
  const params = status && status !== 'all' ? { status } : {};
  const { data } = await apiClient.get('/admin/quotes', { params });
  return data;
};

export const updateQuoteStatus = async (id, status) => {
  const { data } = await apiClient.patch(`/admin/quotes/${id}`, { status });
  return data;
};

export const deleteQuote = async (id) => {
  const { data } = await apiClient.delete(`/admin/quotes/${id}`);
  return data;
};

export const exportQuotesCsvUrl = () => `${API}/admin/quotes.csv`;

// ============ Payments / Shop ============
export const createCheckoutSession = async (payload) => {
  const { data } = await apiClient.post('/payments/checkout', payload);
  return data;
};

export const fetchPaymentStatus = async (sessionId) => {
  const { data } = await apiClient.get(`/payments/status/${sessionId}`);
  return data;
};

export const fetchOrderBySession = async (sessionId) => {
  const { data } = await apiClient.get(`/orders/by-session/${sessionId}`);
  return data;
};


// ============ Auth ============
export const loginWithPassword = async ({ email, password }) => {
  const { data } = await apiClient.post('/auth/login', { email, password });
  return data;
};

export const exchangeGoogleSession = async (sessionId) => {
  const { data } = await apiClient.post('/auth/google/session', null, {
    headers: { 'X-Session-ID': sessionId },
  });
  return data;
};

export const logoutApi = async () => {
  const { data } = await apiClient.post('/auth/logout');
  return data;
};

export const fetchMe = async () => {
  const { data } = await apiClient.get('/auth/me');
  return data;
};

// ============ Admin: Inquiries ============
export const fetchAdminInquiries = async (status) => {
  const params = status && status !== 'all' ? { status } : {};
  const { data } = await apiClient.get('/admin/inquiries', { params });
  return data;
};

export const updateInquiryStatus = async (id, status) => {
  const { data } = await apiClient.patch(`/admin/inquiries/${id}`, { status });
  return data;
};

export const deleteInquiry = async (id) => {
  const { data } = await apiClient.delete(`/admin/inquiries/${id}`);
  return data;
};

export const exportInquiriesCsvUrl = () => `${API}/admin/inquiries.csv`;

// ============ Admin: Wholesale Applications ============
export const fetchWholesaleApplications = async (filters = {}) => {
  const params = {};
  if (filters.status && filters.status !== 'all') params.status = filters.status;
  if (filters.tier && filters.tier !== 'all') params.tier = filters.tier;
  if (filters.business_type && filters.business_type !== 'all') params.business_type = filters.business_type;
  const { data } = await apiClient.get('/admin/wholesale-applications', { params });
  return data;
};

export const updateWholesaleApplication = async (id, updates) => {
  const { data } = await apiClient.patch(`/admin/wholesale-applications/${id}`, updates);
  return data;
};

export const deleteWholesaleApplication = async (id) => {
  const { data } = await apiClient.delete(`/admin/wholesale-applications/${id}`);
  return data;
};

export const bulkWholesaleApplications = async (payload) => {
  const { data } = await apiClient.post('/admin/wholesale-applications/bulk', payload);
  return data;
};

export const exportWholesaleApplicationsCsvUrl = () => `${API}/admin/wholesale-applications.csv`;

// ============ Admin: Products ============
export const createProduct = async (payload) => {
  const { data } = await apiClient.post('/admin/products', payload);
  return data;
};

export const updateProduct = async (id, payload) => {
  const { data } = await apiClient.patch(`/admin/products/${id}`, payload);
  return data;
};

export const deleteProduct = async (id) => {
  const { data } = await apiClient.delete(`/admin/products/${id}`);
  return data;
};

export const uploadImage = async (file) => {
  const form = new FormData();
  form.append('file', file);
  const { data } = await axios.post(`${API}/admin/upload`, form, {
    withCredentials: true,
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
};

export const fileUrl = (storagePath) => `${API}/files/${storagePath}`;

// ============ Admin: Image cache warmup ============
// Triggers backfill of the on-disk WebP cache for every product image.
// Idempotent — safe to call from the admin dashboard after each deploy or
// whenever thumbnails feel sluggish for a first-time visitor.
export const warmImageCache = async () => {
  const { data } = await apiClient.post('/admin/images/backfill');
  return data;
};

// ============ Admin: Reorder reminder scan ============
// Fires the 48h subscription pre-delivery reminder sweep on demand.
// The scheduled scanner also runs every 6 hours automatically.
export const scanReorderReminders = async ({ dryRun = false } = {}) => {
  const { data } = await apiClient.post('/admin/reorder-reminders/scan', null, {
    params: { dry_run: dryRun },
  });
  return data;
};

// ============ Admin: Overview / Dashboard ============
export const fetchAdminStats = async () => {
  const { data } = await apiClient.get('/admin/overview/stats');
  return data;
};

export const fetchRevenueTimeline = async (days = 30) => {
  const { data } = await apiClient.get('/admin/overview/revenue-timeline', { params: { days } });
  return data;
};

export const fetchTopProducts = async (limit = 5) => {
  const { data } = await apiClient.get('/admin/overview/top-products', { params: { limit } });
  return data;
};

export const fetchRecentActivity = async (limit = 20) => {
  const { data } = await apiClient.get('/admin/overview/activity', { params: { limit } });
  return data;
};

// ============ Admin: Customers ============
export const fetchAdminCustomers = async ({ q = '', limit = 200 } = {}) => {
  const params = { limit };
  if (q) params.q = q;
  const { data } = await apiClient.get('/admin/customers', { params });
  return data;
};

export const fetchAdminCustomerDetail = async (email) => {
  const { data } = await apiClient.get(`/admin/customers/${encodeURIComponent(email)}`);
  return data;
};

// ============ Admin: Subscriptions ============
export const fetchAdminSubscriptions = async () => {
  const { data } = await apiClient.get('/admin/subscriptions');
  return data;
};

export const cancelAdminSubscription = async (subscriptionId) => {
  const { data } = await apiClient.post(`/admin/subscriptions/${subscriptionId}/cancel`);
  return data;
};

export const resumeAdminSubscription = async (subscriptionId) => {
  const { data } = await apiClient.post(`/admin/subscriptions/${subscriptionId}/resume`);
  return data;
};

// ============ Admin: Analytics ============
export const fetchAdminAnalytics = async (days = 90) => {
  const { data } = await apiClient.get('/admin/analytics/deep', { params: { days } });
  return data;
};

export const fetchAdminSettings = async () => {
  const { data } = await apiClient.get('/admin/settings');
  return data;
};

export const updateAdminSettings = async (payload) => {
  const { data } = await apiClient.post('/admin/settings', payload);
  return data;
};

// ============ Admin: Global search ============
export const adminGlobalSearch = async (q) => {
  const { data } = await apiClient.get('/admin/search', { params: { q } });
  return data;
};

// ============ Admin: Bulk actions ============
export const bulkInquiries = async ({ action, ids, status }) => {
  const { data } = await apiClient.post('/admin/inquiries/bulk', { action, ids, status });
  return data;
};

export const bulkQuotes = async ({ action, ids, status }) => {
  const { data } = await apiClient.post('/admin/quotes/bulk', { action, ids, status });
  return data;
};

// ============ Error helpers ============
export function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}
